import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import ErrorPopup, { ErrorPopupProps } from '../components/ErrorPopup/ErrorPopup';

interface ErrorPopupContext {
  addError?: (props: ErrorPopupProps) => void;
  isOpen: boolean;
  clearError?: () => void;
}

const ErrorPopupCtx = createContext<ErrorPopupContext>({ isOpen: false });

export const ErrorPopupProvider = ({ children }: { children: React.ReactNode }) => {
  const [errorQueue, setErrorQueue] = useState<ErrorPopupProps[]>([]);
  const [activeError, setActiveError] = useState<ErrorPopupProps>();

  const addError = useCallback((props: ErrorPopupProps) => {
    setErrorQueue((prevQueue) => [...prevQueue, props]);
  }, []);

  const clearError = useCallback(() => {
    setActiveError(undefined);
  }, []);

  useEffect(() => {
    if (errorQueue.length === 0 || activeError) return;
    setActiveError(errorQueue.pop());
  }, [activeError, errorQueue]);

  const errorPopup = activeError ? <ErrorPopup {...activeError} /> : null;

  return (
    <ErrorPopupCtx.Provider value={{ addError, clearError, isOpen: activeError !== undefined }}>
      {children}
      {errorPopup}
    </ErrorPopupCtx.Provider>
  );
};

export const useAddError = () => {
  const { addError } = useContext(ErrorPopupCtx);
  return useCallback(
    (props: ErrorPopupProps) => {
      if (!addError) return;
      addError(props);
    },
    [addError],
  );
};

export const useClearError = () => {
  const { clearError } = useContext(ErrorPopupCtx);
  return clearError;
};

export const useIsErrorPopupOpen = () => {
  const { isOpen } = useContext(ErrorPopupCtx);
  return isOpen;
};
